import { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { selectCanDo } from '../app/auth.js';
import { selectProjectId } from '../app/projects.js';
import { selectMultipleAnnotations, selectSelectedAnnotationIds } from '../app/interface.js';
import { selectTextAnnotations } from '../app/textAnnotations.js';
import EditModal from './AnnotationEditionModal.jsx';

const headerCellSx = {
  position: 'sticky',
  top: 0,
  backgroundColor: 'white !important',
  background: 'white',
  zIndex: 10,
  padding: '8px',
  textAlign: 'left',
};

const textCellStyle = {
  overflow: 'hidden',
  // trigger by a label whose text has a line break
  // used to show each line in the cell
  whiteSpace: 'pre-line',
  wordWrap: 'break-word',
  padding: '8px',
  textAlign: 'left',
};

const ROW_CLICK_DELAY_MS = 280;

function sameId(a, b) {
  return String(a) === String(b);
}

// trigger by the Labels tab
// used to list the labels and select them like annotation rows
function LabelTable() {
  const dispatch = useDispatch();
  const labels = useSelector(selectTextAnnotations);
  const selectedIds = useSelector(selectSelectedAnnotationIds);
  const projectId = useSelector(selectProjectId);
  const canDo = useSelector(selectCanDo);
  const [showOnlySelected, setShowOnlySelected] = useState(false);
  const lastSelectedRowIdxRef = useRef(null);
  const pendingRowClickTimerRef = useRef(null);
  const isSelected = (id) => selectedIds.some((selectedId) => sameId(selectedId, id));
  // trigger by Show Selection
  // used to list only the selected labels
  const labelsToShow = showOnlySelected ? labels.filter((label) => isSelected(label.id)) : labels;

  const clearPendingRowClick = () => {
    if (pendingRowClickTimerRef.current != null) {
      clearTimeout(pendingRowClickTimerRef.current);
      pendingRowClickTimerRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      if (pendingRowClickTimerRef.current != null) {
        clearTimeout(pendingRowClickTimerRef.current);
      }
    };
  }, []);

  // trigger by a click on a label row
  // used to select that row, a range with shift, or one more row with ctrl
  const handleRowClick = (event, id, rowIdx) => {
    const isShiftPressed = event.shiftKey;
    const isCtrlPressed = event.ctrlKey || event.metaKey;
    let newSelection;

    if (isShiftPressed && lastSelectedRowIdxRef.current !== null && selectedIds.length > 0) {
      const startIdx = Math.min(lastSelectedRowIdxRef.current, rowIdx);
      const endIdx = Math.max(lastSelectedRowIdxRef.current, rowIdx);
      const rangeIds = labelsToShow.slice(startIdx, endIdx + 1).map((label) => label.id);
      const allRangeSelected = rangeIds.every((rangeId) => isSelected(rangeId));
      const currentSelectionSet = new Set(selectedIds);
      if (allRangeSelected) {
        rangeIds.forEach((rangeId) => currentSelectionSet.delete(rangeId));
      } else {
        rangeIds.forEach((rangeId) => currentSelectionSet.add(rangeId));
      }
      newSelection = Array.from(currentSelectionSet);
      lastSelectedRowIdxRef.current = rowIdx;
    } else if (isCtrlPressed) {
      newSelection = isSelected(id)
        ? selectedIds.filter((selectedId) => !sameId(selectedId, id))
        : [...selectedIds, id];
      lastSelectedRowIdxRef.current = rowIdx;
    } else if (isSelected(id) && selectedIds.length === 1) {
      // plain click on the only selected row clears it
      newSelection = [];
      lastSelectedRowIdxRef.current = null;
    } else {
      // plain click selects only that row
      newSelection = [id];
      lastSelectedRowIdxRef.current = rowIdx;
    }

    if (
      newSelection.length !== selectedIds.length ||
      !newSelection.every((selectedId) => isSelected(selectedId))
    ) {
      dispatch(selectMultipleAnnotations(newSelection));
    }
  };

  // trigger by a plain click on a label row
  // used to wait before selecting, so a second click does not undo the first
  const handleRowClickDeferred = (event, id, rowIdx) => {
    clearPendingRowClick();
    if (event.shiftKey || event.ctrlKey || event.metaKey) {
      handleRowClick(event, id, rowIdx);
      return;
    }
    const { shiftKey, ctrlKey, metaKey } = event;
    pendingRowClickTimerRef.current = setTimeout(() => {
      pendingRowClickTimerRef.current = null;
      handleRowClick({ shiftKey, ctrlKey, metaKey }, id, rowIdx);
    }, ROW_CLICK_DELAY_MS);
  };

  return (
    <>
      <div className="headers">
        <p>
          <b>Selection</b>:&nbsp;{selectedIds.length} label{selectedIds.length > 1 ? 's' : ''} selected
          &nbsp;{labelsToShow.length > 0 && (
            <button type="button" onClick={() => dispatch(selectMultipleAnnotations(labelsToShow.map((label) => label.id)))}>
              Select all
            </button>
          )}
          &nbsp;<button type="button" onClick={() => {
            lastSelectedRowIdxRef.current = null;
            dispatch(selectMultipleAnnotations([]));
          }}>Unselect all</button>
          &nbsp;<button type="button" onClick={() => setShowOnlySelected((prev) => !prev)}>
            {showOnlySelected ? "Show All" : "Show Selection"}
          </button>
        </p>
      </div>
      <TableContainer component={Paper} className="custom-table" style={{ overflowY: 'auto' }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={headerCellSx}>Text</TableCell>
              <TableCell sx={{ ...headerCellSx, textAlign: 'center' }}>
                {projectId && canDo('createAnnotation', { projectId }) ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <EditModal mode="text" />
                  </div>
                ) : null}
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {labelsToShow.map((label, rowIdx) => (
              <TableRow
                key={label.id}
                onClick={(event) => handleRowClickDeferred(event, label.id, rowIdx)}
                style={{
                  height: 50,
                  maxHeight: 50,
                  overflow: 'hidden',
                  backgroundColor: isSelected(label.id) ? 'grey' : 'white',
                  cursor: 'pointer',
                }}
              >
                <TableCell style={textCellStyle}>{label.text?.value}</TableCell>
                <TableCell style={{ padding: '8px' }} />
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default LabelTable;
